import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LogoLockup } from "@/components/Logo";
import { toast } from "sonner";

export default function ProfileSetup() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ full_name: "", display_name: "", niche: "", country: "", skills: "", linkedin: "", upwork: "", bio: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["full_name", "display_name", "niche", "country", "skills"]) if (!f[k].trim()) return toast.error("Please complete all required fields");
    setBusy(true);
    try {
      await api.put("/profile/me", { ...f, skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean) });
      await refresh();
      toast.success("Profile complete! Welcome aboard.");
      nav("/");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6"><LogoLockup /></div>
        <div className="bg-white border border-border rounded-2xl shadow-sm p-6 sm:p-8">
          <h1 className="font-heading text-2xl font-bold text-center">Complete your profile</h1>
          <p className="text-sm text-muted-foreground text-center mt-1">A few quick details before you start learning.</p>
          <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4">
            <div><Label>Full name *</Label><Input value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="ps-name" /></div>
            <div><Label>Display name / Nickname *</Label><Input value={f.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="ps-display" /></div>
            <div><Label>VA Niche / Focus *</Label><Input value={f.niche} onChange={(e) => set("niche", e.target.value)} data-testid="ps-niche" /></div>
            <div><Label>Country / Location *</Label><Input value={f.country} onChange={(e) => set("country", e.target.value)} data-testid="ps-country" /></div>
            <div className="sm:col-span-2"><Label>Skills (comma separated) *</Label><Input value={f.skills} onChange={(e) => set("skills", e.target.value)} data-testid="ps-skills" /></div>
            <div><Label>LinkedIn (optional)</Label><Input value={f.linkedin} onChange={(e) => set("linkedin", e.target.value)} data-testid="ps-linkedin" /></div>
            <div><Label>Upwork (optional)</Label><Input value={f.upwork} onChange={(e) => set("upwork", e.target.value)} data-testid="ps-upwork" /></div>
            <div className="sm:col-span-2"><Label>Short bio (optional)</Label><Textarea value={f.bio} onChange={(e) => set("bio", e.target.value)} data-testid="ps-bio" /></div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={busy} className="w-full rounded-full bg-teal hover:bg-teal/90" data-testid="ps-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Finish & Enter
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
